// The areas a ruler can mark: a circle, a cone and a line, sized in whole cells
// from the origin cell's centre. They tint the cells whose centre falls inside
// and decide nothing; the table says who is hit.

export type AreaShape = "circle" | "cone" | "line";
export type RulerShape = "ruler" | AreaShape;
export const RULER_SHAPES: readonly RulerShape[] = ["ruler", "circle", "cone", "line"];

type Cell = { col: number; row: number };
type Point = { x: number; y: number };
type Rect = { x: number; y: number; width: number; height: number };
export type AreaMath = {
	grid: { kind: string; cell_w: number; cell_h: number; cell_distance: number };
	centerOf: (col: number, row: number) => Point;
	visibleCells: (rect: Rect) => Cell[];
	distance: (from: Cell, to: Cell) => number;
};
export type Area = {
	shape: AreaShape;
	// Whole cells from the origin to the far end, at least one, and the distance they cover.
	cells: number;
	amount: number;
	// A cone's width at its far end and a line's width, in the grid's unit of distance.
	width: number;
	origin: Point;
	tip: Point;
	outline: { kind: "circle"; rx: number; ry: number } | { kind: "polygon"; points: number[] };
	hits: Cell[];
};

export const knownShape = (value: unknown): RulerShape => (RULER_SHAPES.includes(value as RulerShape) ? (value as RulerShape) : "ruler");

// Half the length, unless the cone is told otherwise.
export const coneWidth = (cells: number, cellDistance: number) => (cells * cellDistance) / 2;

// The widest cone end MapChannel relays, in the grid's unit.
export const MAX_WIDTH = 1000;

// A width typed as a person writes it, comma or dot, in the hundredths the table is sent;
// null for blank, unusable, or one that rounds to nothing or past the widest.
export function parseWidth(text: string) {
	const value = Math.round(Number.parseFloat(text.trim().replace(",", ".")) * 100) / 100;
	return Number.isFinite(value) && value > 0 && value <= MAX_WIDTH ? value : null;
}

// Past this many candidate cells only the outline is drawn, so a stray huge area stays cheap.
export const MAX_TINTED = 20_000;
// A rounded calibration puts a cell centre a hair past the reach, and the error grows with the reach.
const slackFor = (cells: number) => cells * 1e-3 + 0.01;

// `to` only gives the direction and the length, rounded to whole cells; `bounds` is the scene.
// Squares are measured in cells along each axis, so a cell wider than tall still counts its rows.
export function areaOf(math: AreaMath, shape: AreaShape, from: Cell, to: Cell, { width, bounds }: { width?: number | null; bounds: Rect }): Area {
	const { grid } = math;
	const hex = grid.kind === "hex";
	const sx = hex ? grid.cell_h : grid.cell_w;
	const sy = grid.cell_h;
	const origin = math.centerOf(from.col, from.row);
	const end = math.centerOf(to.col, to.row);
	const gap = Math.hypot((end.x - origin.x) / sx, (end.y - origin.y) / sy);
	const cells = Math.max(1, Math.round(gap));
	const ux = gap ? (end.x - origin.x) / sx / gap : 1;
	const uy = gap ? (end.y - origin.y) / sy / gap : 0;
	const tip = { x: origin.x + ux * cells * sx, y: origin.y + uy * cells * sy };
	const wide = shape === "cone" ? (width ?? coneWidth(cells, grid.cell_distance)) : grid.cell_distance;
	const half = wide / grid.cell_distance / 2;

	let outline: Area["outline"];
	let box: Rect;
	if (shape === "circle") {
		outline = { kind: "circle", rx: cells * sx, ry: cells * sy };
		box = { x: origin.x - cells * sx, y: origin.y - cells * sy, width: 2 * cells * sx, height: 2 * cells * sy };
	} else {
		const px = -uy * half * sx;
		const py = ux * half * sy;
		const points =
			shape === "cone"
				? [origin.x, origin.y, tip.x + px, tip.y + py, tip.x - px, tip.y - py]
				: [origin.x + px, origin.y + py, tip.x + px, tip.y + py, tip.x - px, tip.y - py, origin.x - px, origin.y - py];
		outline = { kind: "polygon", points };
		const xs = points.filter((_, index) => index % 2 === 0);
		const ys = points.filter((_, index) => index % 2 === 1);
		box = { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
	}

	const slack = slackFor(cells);
	const inside = (point: Point) => {
		const dx = (point.x - origin.x) / sx;
		const dy = (point.y - origin.y) / sy;
		if (shape === "circle") return Math.hypot(dx, dy) <= cells + slack;
		const along = dx * ux + dy * uy;
		const across = Math.abs(dy * ux - dx * uy);
		return along > slack && along <= cells + slack && across <= (shape === "cone" ? (half * along) / cells : half) + slack;
	};
	const left = Math.max(box.x, bounds.x);
	const top = Math.max(box.y, bounds.y);
	const right = Math.min(box.x + box.width, bounds.x + bounds.width);
	const bottom = Math.min(box.y + box.height, bounds.y + bounds.height);
	// What visibleCells would list, counted before it is asked to.
	const candidates = ((right - left) / (hex ? 0.75 * grid.cell_w : grid.cell_w) + 3) * ((bottom - top) / grid.cell_h + 3);
	const hits =
		right < left || bottom < top || candidates > MAX_TINTED
			? []
			: math.visibleCells({ x: left, y: top, width: right - left, height: bottom - top }).filter((cell) => {
					const centre = math.centerOf(cell.col, cell.row);
					return centre.x >= bounds.x && centre.x <= bounds.x + bounds.width && centre.y >= bounds.y && centre.y <= bounds.y + bounds.height && inside(centre);
				});
	return { shape, cells, amount: cells * grid.cell_distance, width: wide, origin, tip, outline, hits };
}
